import { initCards, isCardsReady } from "@dm-online/shared";
import type { CardSnapshot } from "@dm-online/shared";
import { useAuthStore } from "@/stores/authStore";

// カードマスタ（Worker の /api/cards/snapshot）を起動時に取り込む。
// 取得済みは IndexedDB に version 付きで保存し、次回はキャッシュで即初期化 →
// バックグラウンドで ETag(=version) 再検証して、更新があれば差し替える。

const URL_SNAPSHOT = `${import.meta.env.VITE_API_BASE ?? ""}/api/cards/snapshot`;
const DB_NAME = "dm-cards";
const STORE = "snapshot";
const KEY = "current";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// IndexedDB が使えない（プライベートモード等）場合はキャッシュ無しで動く
async function readCache(): Promise<CardSnapshot | null> {
  try {
    const db = await openDb();
    return await new Promise((resolve) => {
      const req = db.transaction(STORE).objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve((req.result as CardSnapshot | undefined) ?? null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function writeCache(snapshot: CardSnapshot): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(snapshot, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    // キャッシュ失敗は無視
  }
}

// 更新があれば新しいスナップショットを、無ければ null を返す。
async function fetchSnapshot(knownVersion?: string): Promise<CardSnapshot | null> {
  const token = useAuthStore.getState().token;
  const res = await fetch(URL_SNAPSHOT, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(knownVersion ? { "If-None-Match": `"${knownVersion}"` } : {}),
    },
  });
  if (res.status === 304) return null;
  if (res.status === 401) {
    useAuthStore.getState().clearAuth();
    window.location.href = "/login?expired=1";
    throw new Error("認証の有効期限が切れました");
  }
  if (!res.ok) throw new Error(`カードデータの取得に失敗しました (${res.status})`);
  return (await res.json()) as CardSnapshot;
}

async function revalidate(knownVersion: string): Promise<void> {
  try {
    const fresh = await fetchSnapshot(knownVersion);
    if (!fresh) return;
    initCards(fresh);
    await writeCache(fresh);
  } catch {
    // 再検証の失敗は、キャッシュのデータで継続する
  }
}

let loading: Promise<void> | null = null;

// カードストアを初期化する（多重呼び出しは1回にまとめる）。失敗時は reject。
export function loadCards(): Promise<void> {
  if (isCardsReady()) return Promise.resolve();
  loading ??= (async () => {
    const cached = await readCache();
    if (cached) {
      initCards(cached);
      void revalidate(cached.version);
      return;
    }
    const snapshot = await fetchSnapshot();
    if (!snapshot) throw new Error("カードデータが空です");
    initCards(snapshot);
    await writeCache(snapshot);
  })().finally(() => {
    loading = null;
  });
  return loading;
}
